/**
 * Company › Workspaces (ADR-022): the workspaces the Company holds. A
 * workspace you own links at once; one you belong to is invited and its
 * owner accepts; only the Company unlinks, immediately.
 *
 * Linking and inviting are open to every Company role — owner and admin are
 * both privileged management roles (ADR-022 amendment 2026-10-07); the
 * workspace's owner still accepts an invitation, and the proposal names the
 * payer. Unlinking stays a Company-OWNER act (it returns billing to whoever
 * paid before), refused server-side for an admin, who is told so here.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import type { CompanySettingsDto } from '@/lib/api';
import { useHolderSwitcher } from '@/components/HolderSwitcher';
import {
  useCancelCompanyLink,
  useLinkOwnedWorkspace,
  useProposeCompanyLink,
  useUnlinkCompanyWorkspace,
} from '../../hooks/useCompanies';
import { describeError, memberLabel } from './shared';

export function WorkspacesSection({
  data,
  isOwner,
}: {
  data: CompanySettingsDto;
  isOwner: boolean;
}) {
  return <WorkspacesCard data={data} isOwner={isOwner} />;
}

export function WorkspacesCard({ data, isOwner }: { data: CompanySettingsDto; isOwner: boolean }) {
  const companyId = data.company.companyId;
  const link = useLinkOwnedWorkspace(companyId);
  const propose = useProposeCompanyLink(companyId);
  const cancel = useCancelCompanyLink(companyId);
  const unlink = useUnlinkCompanyWorkspace(companyId);
  // Two doors, from the Company's side. A workspace YOU OWN links at once —
  // you are both parties. A workspace you merely belong to gets a proposal
  // its owner accepts. (A workspace you have no seat on cannot be picked
  // here; its owner would need to seat you first.)
  const switcher = useHolderSwitcher({ defaultToPersonal: false });
  const linkedIds = new Set(data.workspaces.map((w) => w.holderId));
  const pendingIds = new Set(data.pendingLinks.map((p) => p.holderId));
  const available = switcher.options.filter(
    (o) => o.holderType === 'org' && !linkedIds.has(o.holderId) && !pendingIds.has(o.holderId),
  );
  const owned = available.filter((o) => o.role === 'owner');
  const memberOf = available.filter((o) => o.role !== 'owner');
  const [ownPick, setOwnPick] = useState('');
  const [proposePick, setProposePick] = useState('');
  const [unlinking, setUnlinking] = useState<CompanySettingsDto['workspaces'][number] | null>(null);
  // "Link now" still states what the workspace gives up before it happens —
  // the same two lines the accept screen shows a workspace owner.
  const [linkingOwn, setLinkingOwn] = useState<{ holderId: string; name: string } | null>(null);

  /** Runs `fn`, toasting either way; resolves true only on success. */
  async function run(fn: () => Promise<unknown>, done: string): Promise<boolean> {
    try {
      await fn();
      toast({ title: done });
      return true;
    } catch (err) {
      toast({
        title: 'Could not do that',
        description: describeError(err),
        variant: 'destructive',
      });
      return false;
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Workspaces</CardTitle>
        <CardDescription>
          The workspaces this Company holds. Linking moves a workspace&apos;s billing to the
          Company&apos;s payer at once and lets Company-approved arches work with its data; its
          roster, roles and files stay as they are. Only the Company can unlink, and unlinking is
          immediate.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (ownPick) {
              const name = owned.find((o) => o.holderId === ownPick)?.displayName ?? 'workspace';
              setLinkingOwn({ holderId: ownPick, name });
            }
          }}
        >
          <div className="grid gap-1.5">
            <Label htmlFor="company-link-own">Link a workspace you own</Label>
            <select
              id="company-link-own"
              className="h-9 min-w-[16rem] rounded-md border border-input bg-background px-2 text-sm"
              value={ownPick}
              onChange={(e) => setOwnPick(e.target.value)}
              disabled={owned.length === 0}
            >
              <option value="">{owned.length === 0 ? 'None available' : 'Choose…'}</option>
              {owned.map((c) => (
                <option key={c.holderId} value={c.holderId}>
                  {c.displayName}
                </option>
              ))}
            </select>
          </div>
          <Button type="submit" disabled={!ownPick || link.isPending}>
            {link.isPending ? 'Linking…' : 'Link now'}
          </Button>
          <p className="basis-full text-xs text-muted-foreground">
            You own it, so it links immediately — no one else needs to accept.
          </p>
        </form>

        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (proposePick)
              void run(
                () => propose.mutateAsync(proposePick),
                'Invited — the workspace owner must accept',
              ).then((ok) => ok && setProposePick(''));
          }}
        >
          <div className="grid gap-1.5">
            <Label htmlFor="company-link-propose">Invite a workspace you belong to</Label>
            <select
              id="company-link-propose"
              className="h-9 min-w-[16rem] rounded-md border border-input bg-background px-2 text-sm"
              value={proposePick}
              onChange={(e) => setProposePick(e.target.value)}
              disabled={memberOf.length === 0}
            >
              <option value="">{memberOf.length === 0 ? 'None available' : 'Choose…'}</option>
              {memberOf.map((c) => (
                <option key={c.holderId} value={c.holderId}>
                  {c.displayName}
                </option>
              ))}
            </select>
          </div>
          <Button type="submit" disabled={!proposePick || propose.isPending}>
            Invite
          </Button>
          <p className="basis-full text-xs text-muted-foreground">
            Its owner accepts the invitation from Workspace Controls; it lapses after 14 days. A
            workspace you have no seat on cannot be invited from here.
            {!isOwner &&
              " Because you are a Company admin, the Company's owners and its payer are emailed when it is accepted."}
          </p>
        </form>

        {data.pendingLinks.length > 0 && (
          <section className="space-y-2">
            <h3 className="-mx-6 flex items-center justify-between border-y border-border bg-muted px-6 py-2 text-sm font-semibold text-foreground">
              <span>Invited — awaiting the workspace owner</span>
              <span className="font-normal text-muted-foreground">{data.pendingLinks.length}</span>
            </h3>
            <ul className="space-y-1 text-sm">
              {data.pendingLinks.map((p) => (
                <li key={p.invitationId} className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">
                    {switcher.options.find((o) => o.holderId === p.holderId)?.displayName ??
                      `workspace …${p.holderId.slice(-6)}`}{' '}
                    · lapses {new Date(p.expiresAt).toLocaleDateString()}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      void run(
                        () =>
                          cancel.mutateAsync({
                            invitationId: p.invitationId,
                            holderId: p.holderId,
                          }),
                        'Invitation withdrawn',
                      )
                    }
                    disabled={cancel.isPending}
                  >
                    Withdraw
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="space-y-2">
          <h3 className="-mx-6 flex items-center justify-between border-y border-border bg-muted px-6 py-2 text-sm font-semibold text-foreground">
            <span>Linked Company Workspaces</span>
            <span className="font-normal text-muted-foreground">{data.workspaces.length}</span>
          </h3>
          {!isOwner && data.workspaces.length > 0 && (
            <p className="text-sm text-muted-foreground">
              Only an owner can unlink a workspace — it returns billing to whoever paid before.
            </p>
          )}
          {data.workspaces.length === 0 ? (
            <p className="text-sm text-muted-foreground">No workspaces linked yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-border border-y border-border">
              {data.workspaces.map((w) => (
                <li
                  key={w.holderId}
                  className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">
                      {w.displayName}
                      {w.archivedAt && (
                        <Badge
                          variant="outline"
                          className="ml-2"
                          title="Its owner deleted the workspace. It stays linked — and billing your payer — until you unlink it; its owner can also restore it."
                        >
                          Deleted by its owner
                        </Badge>
                      )}
                    </p>
                    {w.missingOwnerPrincipalIds.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Not on this workspace&apos;s roster:{' '}
                        <span className="text-foreground">
                          {w.missingOwnerPrincipalIds
                            .map((id) => {
                              const m = data.members.find((x) => x.principalId === id);
                              return m ? memberLabel(m) : `…${id.slice(-6)}`;
                            })
                            .join(', ')}
                        </span>{' '}
                        — its owner adds them from Workspace Controls.
                      </p>
                    )}
                  </div>
                  {isOwner && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setUnlinking(w)}
                      disabled={unlink.isPending}
                    >
                      Unlink
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </CardContent>
      <AlertDialog open={linkingOwn !== null} onOpenChange={(o) => !o && setLinkingOwn(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Link &ldquo;{linkingOwn?.name}&rdquo; to this Company?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>You own it, so it links immediately — no one else needs to accept.</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>Its billing moves to the Company&apos;s payer immediately — no proration.</li>
                  <li>Only the Company can unlink it afterwards.</li>
                  <li>
                    Arches the Company approves can read its data on behalf of its members, at each
                    member&apos;s own role. Its roster, roles and files stay as they are.
                  </li>
                  {!isOwner && (
                    <li>
                      You are a Company admin, so the Company&apos;s owners and its payer are
                      emailed that this workspace was linked.
                    </li>
                  )}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={link.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={link.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!linkingOwn) return;
                const target = linkingOwn;
                void run(() => link.mutateAsync(target.holderId), `Linked ${target.name}`).then(
                  (ok) => {
                    setLinkingOwn(null);
                    if (ok) setOwnPick('');
                  },
                );
              }}
            >
              {link.isPending ? 'Linking…' : 'Link now'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={unlinking !== null} onOpenChange={(o) => !o && setUnlinking(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink &ldquo;{unlinking?.displayName}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              Immediately: its billing returns to whoever paid for it before it linked, and arches
              approved at Company level stop applying to it. Company owners seated as its admins
              when it linked are removed from its roster. Its files, roster and roles are untouched,
              and approvals it made for itself stay. Linking it again needs a new invitation its
              owner accepts.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unlink.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={unlink.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!unlinking) return;
                const w = unlinking;
                void run(() => unlink.mutateAsync(w.holderId), `Unlinked ${w.displayName}`).then(
                  () => setUnlinking(null),
                );
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {unlink.isPending ? 'Unlinking…' : 'Unlink'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
